import {
  GetBrowseCategoriesResponse,
  GetBrowseCategoriesResponse_CategoryTile,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/browse_page/v1/service.types';
import {originClick} from '@aboutyou/legacy-tracking-sdk';
import {useTrackOriginClick} from '@aboutyou/tracking';
import {
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  Image,
  SaleCloudIcon,
  Text,
  UnstyledLink,
  getSolidOrGradientLinearBGColor,
} from '@aboutyou/ui-components';
import React, {memo} from 'react';
import styled, {css} from 'styled-components';
import {CouponHintIcon} from '../../../features/Coupons/CouponHintIcon/CouponHintIcon';
import couponTilePattern from '../../../features/Coupons/assets/couponTilePattern.svg';
import {useHighTierCoupon} from '../../../features/Coupons/useHighTierCoupon';
import {useOffcanvasOpenState} from '../../Navigation/useOffcanvasOpenState';

const imageWidths = [75, 150, 300];
const breakpoints = [
  {
    columns: 3,
  },
];

export interface CategoryTilesViewProps {
  sectionIndex: number;
  className?: string;
  enableKidsCoverImage?: boolean;
}

const CategoryTilesView: React.FC<
  GetBrowseCategoriesResponse & CategoryTilesViewProps
> = ({gradientColors, categoryTiles, tracker, className}) => {
  const {open: openOffcanvas} = useOffcanvasOpenState();
  const colorsArray = gradientColors.map(color => color.hex);
  const trackOriginClick = useTrackOriginClick()(tracker.name as originClick);

  const highTierCoupon = useHighTierCoupon().data;
  const highTierCouponLink = highTierCoupon?.highTierCouponBanner?.link;

  const onCardClick = () => {
    trackOriginClick();
  };

  return (
    <Wrapper data-testid="browseCategoryTiles" className={className}>
      {categoryTiles.map((tile, index) => {
        const tileCategoryId = tile.categoryId;
        let dataTestId = `browseCategoryTile${
          tileCategoryId ? `-${tileCategoryId}` : 'Link'
        }`;

        if (tile.type?.$case === 'character') {
          dataTestId += '-campaign';
        } else if (tile.type?.$case === 'couponIcon') {
          dataTestId += '-coupon';
        }

        const newLink =
          tile.type?.$case === 'couponIcon' && highTierCouponLink
            ? highTierCouponLink
            : tile.link;

        return (
          <TileLink
            key={`browse-category-tile-${tileCategoryId}-${index}`}
            data-testid={dataTestId}
            data-is-coupon={tile.type?.$case === 'couponIcon'}
            to={newLink.url}
            openExternalLinkInNewTab={newLink.external}
            aria-label={undefined}
            onClick={e => {
              if (
                !tileCategoryId ||
                !tile.drawOffCanvas ||
                tile.type?.$case === 'couponIcon'
              ) {
                return;
              }

              e.preventDefault();

              onCardClick();
              openOffcanvas({
                parameter: String(tileCategoryId),
                replaceHistory: false,
              });
            }}
          >
            <TileContainer
              data-center-content={tile.type?.$case === 'character'}
              data-is-coupon={tile.type?.$case === 'couponIcon'}
              style={{
                background:
                  tile.type?.$case === 'character'
                    ? getSolidOrGradientLinearBGColor(colorsArray)
                    : undefined,
              }}
            >
              {tile.reductionType?.$case === 'discountReduction' && (
                <StyledSaleCloudIcon
                  colors={colorsArray}
                  data-testid="browseCategoryTile-saleIcon"
                />
              )}
              {tile.reductionType?.$case === 'couponReduction' && (
                <CouponHintIcon data-testid="browseCategoryTile-couponIcon" />
              )}
              <Tile tile={tile} />
            </TileContainer>
            <Label weight="mediumHeavy" $textColor={tile.labelColor?.hex}>
              {tile.label}
            </Label>
          </TileLink>
        );
      })}
    </Wrapper>
  );
};

const Tile = (props: {tile: GetBrowseCategoriesResponse_CategoryTile}) => {
  switch (props.tile.type?.$case) {
    case 'image': {
      return (
        <ImageSpacer removePadding={props.tile.isCoverImage}>
          <StyledImage
            src={props.tile.type.image.url}
            alt={props.tile.type.image.description}
            breakpoints={breakpoints}
            widths={imageWidths}
            importance="low"
            ratio="1:1"
          />
        </ImageSpacer>
      );
    }
    case 'character': {
      return (
        <CampaignCharacter weight="heavy" color="White">
          {props.tile.type?.character}
        </CampaignCharacter>
      );
    }
    case 'couponIcon': {
      return (
        <IconWrapper data-testid="couponTile">
          <Icon
            asset={GenericIconName.Coupon}
            color="White"
            size="L"
            fill="White"
          />
        </IconWrapper>
      );
    }
    default:
      return null;
  }
};

const CampaignCharacter = styled(Text)`
  font-size: clamp(${FONT_SIZES.px40}, 10vw, ${FONT_SIZES.px48});
`;

export const WrapperCSS = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-content: space-between;
  padding: 0 1rem;
  gap: 1rem 0.5rem;
`;

export const TileCSS = css`
  text-align: center;
`;

const IconWrapper = styled.div`
  color: ${COLORS.White};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  ${WrapperCSS}
`;

const TileLink = styled(UnstyledLink)`
  ${TileCSS}
`;

const TileContainer = styled.div`
  aspect-ratio: 1 / 1;
  background-color: ${COLORS.ProductImageBackground};
  border-radius: 8px;
  display: flex;
  flex: 1;
  flex-basis: 100%;
  justify-content: center;
  margin-bottom: 0.5rem;
  overflow: hidden;
  position: relative;
  min-width: 100%;

  &[data-center-content='true'] {
    align-items: center;
    justify-content: center;
  }

  &[data-is-coupon='true'] {
    background-color: ${COLORS.PrimaryCoupon};
    background-image: url('${couponTilePattern}');
    background-size: cover;
  }
`;

const ImageSpacer = styled.div<{removePadding?: boolean}>`
  ${({removePadding}) =>
    !removePadding &&
    css`
      padding: 1.2rem;
    `}
`;

const StyledImage = styled(Image)`
  object-fit: contain;
`;

const Label = styled(Text)<{$textColor?: string}>`
  overflow-wrap: anywhere;
  hyphens: auto;

  ${({$textColor}) =>
    $textColor &&
    css`
      color: ${$textColor};
    `};

  [data-is-coupon='true'] & {
    color: ${COLORS.PrimaryCoupon};
    hyphens: unset;
    overflow-wrap: unset;
  }
`;

const StyledSaleCloudIcon = styled(SaleCloudIcon)`
  aspect-ratio: 1 / 1;
  position: absolute;
  top: -22%;
  width: 50%;
  left: -22%;
`;

const MemoCategoryTilesView = memo(CategoryTilesView);

export {MemoCategoryTilesView as CategoryTilesView};
